import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Clock, FileText, Loader2 } from 'lucide-react';

import type {
  LandingDraftCampaignGroup,
  LandingDraftSummary,
} from '../../services/landingPagesService';

/**
 * History of generated drafts: campaign -> date -> runs.
 *
 * ⚠ WHY THIS EXISTS. Until AGCO-275 a generated page lived in one browser
 * tab's memory and nowhere else. Reload and it was gone — no list, no way to
 * reopen one, and no way to compare two. Reviewing the agileandco draft meant
 * saving HTML files by hand.
 *
 * The shape is the operator's, not the schema's (Jacob, 2026-08-19): a page is
 * built FOR a campaign, several times, over several days. Campaign is the
 * thing you remember, the date narrows it, and the run is what you open.
 *
 * ⚠ THE "NOT TIED TO A CAMPAIGN" GROUP IS FIRST CLASS, NOT A FALLBACK BIN. A
 * hand-typed service has no campaign — which is exactly how every agileandco
 * draft in this feature's own review was generated, because "Online Marketing
 * Agency" is not one of the seven services on that client's record. A design
 * that dropped campaign-less runs would have discarded all of them.
 */

const timeOf = (iso: string) => {
  // Times are shown in the VIEWER's zone on purpose: "when did I run this"
  // is a local question. The DATE grouping is server-side precisely so the
  // buckets do not move between viewers while the times inside them read
  // naturally.
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso.slice(11, 16);
  }
};

const dayLabel = (isoDate: string) => {
  const today = new Date();
  const toKey = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
  if (isoDate === toKey(today)) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (isoDate === toKey(yesterday)) return 'Yesterday';
  try {
    // Parsed as local midnight, not UTC — `new Date('2026-08-19')` is UTC and
    // renders as the previous day for anyone west of Greenwich.
    const [y, m, d] = isoDate.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return isoDate;
  }
};

/** Findings worth seeing at a glance, without reopening the page. */
const findingCount = (qa: Record<string, unknown>) => {
  const bucket = (k: string) => (Array.isArray(qa?.[k]) ? (qa[k] as unknown[]).length : 0);
  return {
    blocking: bucket('fatal') + bucket('data_gaps'),
    other: bucket('suppressible') + bucket('warns'),
  };
};

function RunRow({
  run,
  active,
  onOpen,
}: {
  run: LandingDraftSummary;
  active: boolean;
  onOpen: (id: string) => void;
}) {
  const counts = findingCount(run.qa || {});
  return (
    <button
      type="button"
      onClick={() => onOpen(run.id)}
      aria-current={active ? 'true' : undefined}
      className={`flex w-full items-start gap-3 rounded px-2 py-2 text-left text-sm transition-colors ${
        active ? 'bg-coral-light text-coral-dark' : 'hover:bg-teal-light'
      }`}
    >
      <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium tabular-nums">{timeOf(run.created_at)}</span>
          <span className="text-muted-foreground">
            {run.template_slug.replace(/^landing-/, '').replace(/-/g, ' ')}
          </span>
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {run.target_keyword || run.service_name}
        </span>
        <span className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          {counts.blocking > 0 && (
            <span className="rounded bg-destructive/10 px-1.5 py-0.5 text-destructive">
              {counts.blocking} to fix
            </span>
          )}
          {counts.other > 0 && (
            <span className="rounded bg-teal-light px-1.5 py-0.5 text-teal-dark">
              {counts.other} noted
            </span>
          )}
          {run.published_post_id != null && (
            <span className="rounded bg-coral-light px-1.5 py-0.5 text-coral-dark">
              published
            </span>
          )}
          <span className="text-muted-foreground tabular-nums">
            {Math.round(run.html_bytes / 1024)}KB
          </span>
        </span>
      </span>
    </button>
  );
}

function CampaignGroup({
  group,
  openId,
  onOpen,
  defaultOpen,
}: {
  group: LandingDraftCampaignGroup;
  openId: string | null;
  onOpen: (id: string) => void;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const key = group.campaign_resource_name ?? '__none__';
  return (
    <li className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-2 py-2.5 text-left hover:bg-teal-light"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 flex-1">
          <span
            className={`block truncate text-sm font-medium ${
              group.is_unassigned ? 'text-muted-foreground' : ''
            }`}
            title={group.campaign_name}
          >
            {group.campaign_name}
          </span>
        </span>
        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
          {group.run_count}
        </span>
      </button>

      {open && (
        <ul className="pb-2 pl-6 pr-2">
          {group.dates.map((d) => (
            <li key={`${key}-${d.date}`} className="mt-1">
              <p className="px-2 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {dayLabel(d.date)}
              </p>
              <ul>
                {d.runs.map((r) => (
                  <li key={r.id}>
                    <RunRow run={r} active={r.id === openId} onOpen={onOpen} />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function DraftHistory({
  groups,
  total,
  isLoading,
  isError,
  openId,
  onOpen,
}: {
  groups: LandingDraftCampaignGroup[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  openId: string | null;
  onOpen: (id: string) => void;
}) {
  // Only the first group starts expanded. Opening everything on a tenant with
  // a dozen campaigns turns the panel into a wall.
  const firstKey = useMemo(
    () => (groups.length ? groups[0].campaign_resource_name ?? '__none__' : null),
    [groups]
  );

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading history…
      </p>
    );
  }

  if (isError) {
    // ⚠ SAY WHICH IT IS. "No drafts" and "we could not read them" look
    // identical to an operator, and only one of them means their work is gone.
    return (
      <p className="px-2 py-3 text-sm text-destructive">
        Could not load history. The drafts may still be stored — this is a read
        failure, not proof they are missing.
      </p>
    );
  }

  if (!groups.length) {
    return (
      <div className="px-2 py-3 text-sm text-muted-foreground">
        <p className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          No drafts yet for this client.
        </p>
        <p className="mt-1 text-xs">
          Every page you generate is kept here, grouped by campaign and day.
        </p>
      </div>
    );
  }

  return (
    <div>
      <ul className="rounded border border-border bg-card">
        {groups.map((g) => {
          const key = g.campaign_resource_name ?? '__none__';
          return (
            <CampaignGroup
              key={key}
              group={g}
              openId={openId}
              onOpen={onOpen}
              defaultOpen={key === firstKey}
            />
          );
        })}
      </ul>
      <p className="mt-2 px-2 text-xs text-muted-foreground tabular-nums">
        {total} draft{total === 1 ? '' : 's'} stored
      </p>
    </div>
  );
}
